/**
 * Drag and drop in the channel sidebar (2026-10-29), Discord's: a person in
 * voice dropped on another voice channel moves them (Move Members, or
 * yourself: a switch), and channels and categories are sorted by dragging
 * them (Manage Channels). This file is the part without the DOM: who may
 * drag what, where a drop lands, the drop line's place and the commit. The
 * pointer handling is `useSidebarDnd.tsx`; the rules are the ones already
 * built (`store/moveMember.ts`, `store/reorder.ts`) and the server checks
 * them again.
 */
import {
  ChannelType,
  isVoiceChannelType,
  Permissions,
  type Channel,
  type ModifyChannelPositionsRequest,
  type Snowflake,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { api } from '../lib/api';
import { runAction } from '../mod/actions';
import { memberActions } from '../mod/perms';
import { useData } from '../store/data';
import { canMoveMemberTo } from '../store/moveMember';
import type { DataState } from '../store/reducer';
import { channelDropBody, type ChannelDrop } from '../store/reorder';
import { basePermissions, can, channelTree, type CategoryNode } from '../store/selectors';

/** How far (CSS px) the pointer moves before a press becomes a drag: a click stays a click. */
export const DRAG_THRESHOLD = 5;

/** A press that moved this far from where it started is a drag. */
export function pastThreshold(dx: number, dy: number): boolean {
  return dx * dx + dy * dy >= DRAG_THRESHOLD * DRAG_THRESHOLD;
}

/** The band (CSS px) at the list's top and bottom edge where a drag scrolls it. */
export const AUTO_SCROLL_EDGE = 32;
/** The fastest scroll a drag makes, in px per frame, with the pointer at the very edge or past it. */
export const AUTO_SCROLL_MAX = 14;

/**
 * How far the list scrolls this frame while something is dragged with the
 * pointer at `y` (the list spans `top` to `bottom`): up (negative) or down
 * near an edge, faster the closer, nothing in between.
 */
export function autoScrollStep(y: number, top: number, bottom: number): number {
  const speed = (into: number) =>
    Math.ceil((Math.min(AUTO_SCROLL_EDGE, into) / AUTO_SCROLL_EDGE) * AUTO_SCROLL_MAX);
  if (y < top + AUTO_SCROLL_EDGE) return -speed(top + AUTO_SCROLL_EDGE - y);
  if (y > bottom - AUTO_SCROLL_EDGE) return speed(y - (bottom - AUTO_SCROLL_EDGE));
  return 0;
}

/* ---------------- the drag's state (rows read it) ---------------- */

export interface MemberDrag {
  kind: 'member';
  guildId: Snowflake;
  userId: Snowflake;
  /** Their voice channel when the drag started. */
  from: Snowflake;
  /** The voice channels a drop would move them to (`canMoveMemberTo`), lit up. */
  targets: Snowflake[];
  /** The valid target under the pointer, or null. */
  over: Snowflake | null;
}

export interface ChannelDragState {
  kind: 'channel';
  guildId: Snowflake;
  /** The dragged channel or category. */
  id: Snowflake;
  category: boolean;
  /** Where a drop now would land, or null (nowhere new: a drop does nothing). */
  drop: ChannelDrop | null;
  /** The drop line's top in the list's content (px), or null. */
  line: number | null;
}

export type SidebarDrag = MemberDrag | ChannelDragState;

export const useSidebarDrag = create<{ drag: SidebarDrag | null }>(() => ({ drag: null }));

/**
 * A voice channel row's look while someone is dragged: `over` (the target
 * under the pointer), `ok` (a valid target), undefined otherwise (dimmed by
 * the list while a person is dragged).
 */
export function memberTargetState(
  drag: SidebarDrag | null,
  channelId: Snowflake,
): 'over' | 'ok' | undefined {
  if (drag?.kind !== 'member' || !drag.targets.includes(channelId)) return undefined;
  return drag.over === channelId ? 'over' : 'ok';
}

/* ---------------- who may drag what ---------------- */

/**
 * Whether `userId`, shown in voice, can be dragged by you: Move Members in
 * their channel (the owner holds it everywhere), or yourself (a switch,
 * `memberActions`).
 */
export function canDragMember(d: DataState, guildId: Snowflake, userId: Snowflake): boolean {
  return !!d.voiceStates[guildId]?.[userId]?.channel_id && memberActions(d, guildId, userId).move;
}

/** The voice channels a dragged person lights up (in the sidebar's order). */
export function memberDropTargets(
  d: DataState,
  guildId: Snowflake,
  userId: Snowflake,
): Snowflake[] {
  return channelTree(d, guildId)
    .flatMap((n) => n.channels)
    .filter((c) => isVoiceChannelType(c.type) && canMoveMemberTo(d, guildId, userId, c.id))
    .map((c) => c.id);
}

/** Sorting channels by drag: Manage Channels on the server (the owner has it), as the server checks. */
export function canSortChannels(d: DataState, guildId: Snowflake | null): boolean {
  return !!guildId && can(basePermissions(d, guildId), Permissions.MANAGE_CHANNELS);
}

/* ---------------- where a drop lands ---------------- */

/**
 * What is under the pointer while a channel is dragged: a channel row (its
 * upper or lower half), a category header (its upper or lower half), or the
 * empty space below every row.
 */
export type ChannelHit =
  | { kind: 'channel'; id: Snowflake; after: boolean }
  | { kind: 'head'; catId: Snowflake; after: boolean }
  | { kind: 'end' };

/**
 * What is under the pointer while a category is dragged: a section (the
 * channels outside every category, `catId` null, or a category with its
 * channels; its upper or lower half), or the space below them all.
 */
export type CategoryHit =
  { kind: 'section'; catId: Snowflake | null; after: boolean } | { kind: 'end' };

const isCat = (c: Channel) => c.type === ChannelType.GUILD_CATEGORY;
const isVoice = (c: Channel) => isVoiceChannelType(c.type);

function siblingsIn(
  tree: readonly CategoryNode[],
  parentId: Snowflake | null,
  draggedId: Snowflake,
) {
  const node = tree.find((n) => (n.category?.id ?? null) === parentId);
  return (node?.channels ?? []).filter((c) => c.id !== draggedId);
}

/**
 * The drop a hit means for a dragged channel (not a category): before or
 * after the channel under the pointer, among its siblings; a category
 * header's lower half is the top of that category, its upper half the end
 * of the section above (the top level above the first category: out of
 * every category); below everything, the end of the last section. Null over
 * the dragged row itself.
 */
export function channelDropFor(
  tree: readonly CategoryNode[],
  draggedId: Snowflake,
  hit: ChannelHit,
): ChannelDrop | null {
  const at = (parentId: Snowflake | null, index: number): ChannelDrop => ({
    id: draggedId,
    parentId,
    index,
  });
  const endOf = (parentId: Snowflake | null) =>
    at(parentId, siblingsIn(tree, parentId, draggedId).length);
  if (hit.kind === 'channel') {
    if (hit.id === draggedId) return null;
    const node = tree.find((n) => n.channels.some((c) => c.id === hit.id));
    if (!node) return null;
    const parentId = node.category?.id ?? null;
    const i = siblingsIn(tree, parentId, draggedId).findIndex((c) => c.id === hit.id);
    return at(parentId, i + (hit.after ? 1 : 0));
  }
  if (hit.kind === 'head') {
    if (hit.after) return at(hit.catId, 0);
    const k = tree.findIndex((n) => n.category?.id === hit.catId);
    if (k < 0) return null;
    const prev = tree[k - 1];
    return endOf(prev ? (prev.category?.id ?? null) : null);
  }
  const last = tree.at(-1);
  return last ? endOf(last.category?.id ?? null) : null;
}

/**
 * The drop a hit means for a dragged category: its place among the other
 * categories, as a block with its channels. The section outside every
 * category is always first, so a drop on it makes the category the first.
 * Null over the dragged category itself.
 */
export function categoryDropFor(
  tree: readonly CategoryNode[],
  draggedId: Snowflake,
  hit: CategoryHit,
): ChannelDrop | null {
  const cats = tree.flatMap((n) => (n.category && n.category.id !== draggedId ? [n.category] : []));
  if (hit.kind === 'end') return { id: draggedId, parentId: null, index: cats.length };
  if (hit.catId === null) return { id: draggedId, parentId: null, index: 0 };
  if (hit.catId === draggedId) return null;
  const i = cats.findIndex((c) => c.id === hit.catId);
  if (i < 0) return null;
  return { id: draggedId, parentId: null, index: i + (hit.after ? 1 : 0) };
}

/**
 * Where the drop line goes for a drop: between `afterId` and `beforeId`
 * (either may be null at an end) inside `parentId` (a category, or the top
 * level; for a category drop, among the categories). It follows the place
 * the drop really lands: a text channel never lands among voice channels
 * nor the other way round (`channelDropBody` keeps them apart).
 */
export interface DropPlace {
  parentId: Snowflake | null;
  beforeId: Snowflake | null;
  afterId: Snowflake | null;
}

export function dropPlace(
  tree: readonly CategoryNode[],
  dragged: Channel,
  drop: ChannelDrop,
): DropPlace {
  if (isCat(dragged)) {
    const cats = tree.flatMap((n) =>
      n.category && n.category.id !== dragged.id ? [n.category] : [],
    );
    const land = Math.max(0, Math.min(cats.length, drop.index));
    return {
      parentId: null,
      beforeId: cats[land]?.id ?? null,
      afterId: cats[land - 1]?.id ?? null,
    };
  }
  const sibs = siblingsIn(tree, drop.parentId, dragged.id);
  const texts = sibs.filter((c) => !isVoice(c)).length;
  const land = isVoice(dragged)
    ? Math.max(texts, Math.min(sibs.length, drop.index))
    : Math.max(0, Math.min(texts, drop.index));
  return {
    parentId: drop.parentId,
    beforeId: sibs[land]?.id ?? null,
    afterId: sibs[land - 1]?.id ?? null,
  };
}

/* ---------------- the commit ---------------- */

/** The channels with a positions body applied, as the server would store it. */
export function withPositions(
  channels: Readonly<Record<Snowflake, Channel>>,
  body: ModifyChannelPositionsRequest,
): Record<Snowflake, Channel> {
  const next = { ...channels };
  for (const e of body) {
    const c = next[e.id];
    if (!c) continue;
    next[e.id] = {
      ...c,
      ...(e.position !== undefined && e.position !== null ? { position: e.position } : {}),
      ...(e.parent_id !== undefined ? { parent_id: e.parent_id } : {}),
    };
  }
  return next;
}

/**
 * Sends a drop (`channelDropBody` → `PATCH /guilds/{id}/channels`), shown at
 * once: the sidebar takes the new order before the server answers, and a
 * refusal puts back what it changed (only where nothing newer came since);
 * the refusal is told in a toast (`runAction`). The gateway's
 * CHANNEL_UPDATEs confirm a success. False when nothing was sent (the drop
 * changes nothing) or the server refused.
 */
export async function commitChannelDrop(guildId: Snowflake, drop: ChannelDrop): Promise<boolean> {
  const before = useData.getState().channels;
  const body = channelDropBody(before, guildId, drop);
  if (!body.length) return false;
  const optimistic = withPositions(before, body);
  useData.setState({ channels: { ...useData.getState().channels, ...pick(optimistic, body) } });
  const r = await runAction(() => api.um.channelPositions(guildId, body));
  if (r.ok) return true;
  // the rollback: each changed channel back, unless something newer replaced it
  useData.setState((s) => {
    const channels = { ...s.channels };
    for (const e of body) {
      const now = channels[e.id];
      const was = before[e.id];
      if (now && was && now === optimistic[e.id])
        channels[e.id] = { ...now, position: was.position, parent_id: was.parent_id };
    }
    return { channels };
  });
  return false;
}

function pick(channels: Record<Snowflake, Channel>, body: ModifyChannelPositionsRequest) {
  const out: Record<Snowflake, Channel> = {};
  for (const e of body) if (channels[e.id]) out[e.id] = channels[e.id]!;
  return out;
}
